import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
} from "@ngrx/store";
import { CapabilityVizApiActions } from "@skills-app/capabilities-viz/actions";
import { IHierarchy } from "@skills-app/models/hierarchy";
import { ICapability } from "@skills-app/models/capability";
import {
  IThingSpaceGraph,
  ISkillGroupPopularityModel,
} from "@skills-app/models/thingspaceGraph";

export interface ICapabilitiesVizState {
  capabilityhierarchy: IHierarchy[];
  squadHierarchy: IHierarchy[];
  capabilityskillslist: unknown;
  capability: ICapability | null;
  capabilityDraft: IThingSpaceGraph | null;
  skillPopularities: ISkillGroupPopularityModel | null;
  isLoading: boolean;
  capabilityLoading: boolean;
  capabilityDraftLoading: boolean;
  skillPopularitiesLoading: boolean;
}

const initialState: ICapabilitiesVizState = {
  capabilityhierarchy: [],
  capabilityskillslist: {},
  squadHierarchy: [],
  capability: null,
  capabilityDraft: null,
  skillPopularities: null,
  isLoading: false,
  capabilityLoading: false,
  capabilityDraftLoading: false,
  skillPopularitiesLoading: false,
};

export const reducer = createReducer(
  initialState,

  on(
    CapabilityVizApiActions.loadCapabilityHierarchySuccess,
    (state, action) => ({
      ...state,
      capabilityhierarchy: action.capabilityhierarchy,
      isLoading: false,
    }),
  ),

  on(CapabilityVizApiActions.requestCapabilityHierarchy, (state) => ({
    ...state,
    isLoading: true,
  })),

  on(CapabilityVizApiActions.loadSquadHierarchySuccess, (state, action) => ({
    ...state,
    squadHierarchy: action.squadHierarchy,
  })),

  on(CapabilityVizApiActions.loadCapabilityHierarchyFailure, (state) => ({
    ...state,
    isLoading: false,
  })),

  on(
    CapabilityVizApiActions.loadCapabilitySkillsListSuccess,
    (state, action) => ({
      ...state,
      capabilityskillslist: action.capabilityskillslist,
    }),
  ),

  on(CapabilityVizApiActions.requestCapabilityByType, (state) => ({
    ...state,
    capabilityLoading: true,
  })),

  on(CapabilityVizApiActions.loadCapabilityByTypeSuccess, (state, action) => ({
    ...state,
    capability: action.capability,
    capabilityLoading: false,
  })),

  on(CapabilityVizApiActions.loadCapabilityByTypeFailure, (state) => ({
    ...state,
    capability: null,
    capabilityLoading: false,
  })),

  on(CapabilityVizApiActions.clearCapabilityMeta, (state) => ({
    ...state,
    capability: null,
    capabilityLoading: false,
  })),

  on(CapabilityVizApiActions.requestCapabilityDraftByType, (state) => ({
    ...state,
    capabilityDraftLoading: true,
  })),

  on(
    CapabilityVizApiActions.loadCapabilityDraftByTypeSuccess,
    (state, action) => ({
      ...state,
      capabilityDraft: action.draft,
      capabilityDraftLoading: false,
    }),
  ),

  on(CapabilityVizApiActions.loadCapabilityDraftByTypeFailure, (state) => ({
    ...state,
    capabilityDraft: null,
    capabilityDraftLoading: false,
  })),

  on(CapabilityVizApiActions.clearCapabilityDraft, (state) => ({
    ...state,
    capabilityDraft: null,
    capabilityDraftLoading: false,
  })),

  on(CapabilityVizApiActions.requestCapabilitySkillPopularities, (state) => ({
    ...state,
    skillPopularitiesLoading: true,
  })),

  on(
    CapabilityVizApiActions.loadCapabilitySkillPopularitiesSuccess,
    (state, action) => ({
      ...state,
      skillPopularities: action.skillPopularities,
      skillPopularitiesLoading: false,
    }),
  ),

  on(
    CapabilityVizApiActions.loadCapabilitySkillPopularitiesFailure,
    (state) => ({
      ...state,
      skillPopularities: null,
      skillPopularitiesLoading: false,
    }),
  ),

  on(CapabilityVizApiActions.clearCapabilitySkillPopularities, (state) => ({
    ...state,
    skillPopularities: null,
    skillPopularitiesLoading: false,
  })),
);

export const getCapabilitiesVizState =
  createFeatureSelector<ICapabilitiesVizState>("capabilitiesviz");

export const getCapabilityHierarchy = createSelector(
  getCapabilitiesVizState,
  (state) => state.capabilityhierarchy,
);

export const getCapabilitySkillsList = createSelector(
  getCapabilitiesVizState,
  (state) => state.capabilityskillslist,
);

export const getHierarchyLoading = createSelector(
  getCapabilitiesVizState,
  (state) => state.isLoading,
);

export const getSquadHierarchy = createSelector(
  getCapabilitiesVizState,
  (state) => state.squadHierarchy,
);

export const getCapabilityMeta = createSelector(
  getCapabilitiesVizState,
  (state) => state.capability,
);

export const getCapabilityMetaLoading = createSelector(
  getCapabilitiesVizState,
  (state) => state.capabilityLoading,
);

export const getCapabilityDraft = createSelector(
  getCapabilitiesVizState,
  (state) => state.capabilityDraft,
);

export const getCapabilityDraftLoading = createSelector(
  getCapabilitiesVizState,
  (state) => state.capabilityDraftLoading,
);

export const getCapabilitySkillPopularities = createSelector(
  getCapabilitiesVizState,
  (state) => state.skillPopularities,
);

export const getCapabilitySkillPopularitiesLoading = createSelector(
  getCapabilitiesVizState,
  (state) => state.skillPopularitiesLoading,
);
